/**
 * pf-analytics — republishes theme variant events onto Shopify's analytics bus.
 *
 * GA reaches this store through a Shopify web pixel, which runs in a sandboxed
 * iframe with no DOM access, so `on:variant:change` / `on:variant:redirect` are
 * invisible to it. `Shopify.analytics.publish` is the supported bridge: a
 * Custom Pixel (Admin > Settings > Customer events > Add custom pixel)
 * subscribes to the names published here and forwards them to GA4.
 *
 *   analytics.subscribe('pf_variant_selected', (event) => {
 *     const d = event.data;
 *     if (d.interaction === 'initial') return;
 *     gtag('event', 'select_item', {
 *       item_list_name: 'Variant picker',
 *       pf_changed_option: d.changed_option,
 *       pf_changed_value: d.changed_value,
 *       pf_variant_options: d.variant_options,
 *       pf_fallback_from: d.fallback_from,
 *       pf_availability: d.availability,
 *       items: [
 *         {
 *           item_id: d.sku,
 *           item_variant: d.variant_title,
 *           price: d.price,
 *         },
 *       ],
 *     });
 *   });
 *
 *   analytics.subscribe('pf_variant_redirect', (event) => {
 *     gtag('event', 'pf_variant_redirect', event.data);
 *   });
 *
 * Both picker engines emit `on:variant:change` with different variant shapes —
 * the base theme sends a Shopify variant (price in integer cents), the Polyform
 * picker sends an API ProductSchema variant (price a decimal string, plus the
 * full option selection). `variantPayload` normalises both; only the Polyform
 * shape carries per-option detail.
 */

const PRODUCT_PATH = /\/products\/([^/?#]+)/;

/** @returns {string|null} Current product handle, or null off a product page. */
function productHandle() {
  return PRODUCT_PATH.exec(window.location.pathname)?.[1] ?? null;
}

/** @returns {string|null} Shopify product id, from the storefront's own meta. */
function productId() {
  const id = window.ShopifyAnalytics?.meta?.product?.id;
  return id == null ? null : String(id);
}

/**
 * Shopify variants price in integer cents; the Polyform API sends a decimal
 * string. Normalise both to major units so GA reports one currency scale.
 *
 * @param {number|string|null|undefined} price
 * @returns {number|null}
 */
export function toMajorUnits(price) {
  if (typeof price === 'number') return price / 100;
  const parsed = Number.parseFloat(price);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Base theme: boolean. Polyform API: "instock" | "backorder" | "oos".
 * GA dimensions need one type, so collapse to the enum.
 *
 * @param {boolean|string|null|undefined} available
 * @returns {string|null}
 */
export function toAvailability(available) {
  if (typeof available === 'boolean') return available ? 'instock' : 'oos';
  return available ?? null;
}

/**
 * Flatten an option selection into one GA-friendly scalar.
 *
 * @param {Record<string,string>|null|undefined} options
 * @returns {string|null} e.g. "Color=Navy;Size=Medium"
 */
export function optionsToString(options) {
  if (!options) return null;
  return Object.entries(options)
    .map(([name, value]) => `${name}=${value}`)
    .join(';');
}

/**
 * Which option the shopper just moved. Derived by diffing against the previous
 * selection so the picker's event contract doesn't have to carry it.
 *
 * @param {Record<string,string>|null} options
 * @param {Record<string,string>|null} previous
 * @returns {string|null}
 */
function changedOption(options, previous) {
  if (!options || !previous) return null;
  return (
    Object.keys(options).find((name) => previous[name] !== options[name]) ??
    null
  );
}

/**
 * @param {object} detail   `on:variant:change` event detail.
 * @param {Record<string,string>|null} previous  Last seen selection.
 * @returns {object|null}
 */
export function variantPayload(detail, previous) {
  const variant = detail?.variant;
  if (!variant) return null;

  const options = detail?.selection ?? null;
  const changed = changedOption(options, previous);

  return {
    sku: variant.sku ?? null,
    variant_id: variant.id == null ? null : String(variant.id),
    variant_title: options
      ? Object.values(options).join(' / ')
      : (variant.title ?? null),
    variant_options: optionsToString(options),
    changed_option: changed,
    changed_value: changed ? options[changed] : null,
    interaction: previous ? 'select' : 'initial',
    fallback_from: optionsToString(detail?.fallbackFrom),
    price: toMajorUnits(variant.price),
    availability: toAvailability(variant.available),
    product_handle: productHandle(),
    product_id: productId(),
  };
}

/**
 * @param {object} detail   `on:variant:redirect` event detail.
 * @param {Record<string,string>|null} previous  Last seen selection.
 * @returns {object|null}
 */
export function redirectPayload(detail, previous) {
  if (!detail?.handle) return null;

  const options = detail.selection ?? null;
  const changed = changedOption(options, previous);

  return {
    from_handle: productHandle(),
    to_handle: detail.handle,
    changed_option: changed,
    changed_value: changed ? options[changed] : null,
    variant_options: optionsToString(options),
    product_id: productId(),
  };
}

function publish(name, data) {
  if (!data) return;
  try {
    window.Shopify?.analytics?.publish?.(name, data);
  } catch {
    // Analytics must never break the storefront.
  }
}

let previousSelection = null;
let lastKey = null;

document.addEventListener('on:variant:change', (event) => {
  const payload = variantPayload(event.detail, previousSelection);
  if (!payload) return;

  // The picker re-dispatches on:variant:change after its section re-render, so
  // a single selection can arrive twice. Publish genuine transitions only.
  const key = `${payload.variant_id}|${payload.variant_options}`;
  if (key === lastKey) return;
  lastKey = key;
  previousSelection = event.detail?.selection ?? {};

  publish('pf_variant_selected', payload);
});

document.addEventListener('on:variant:redirect', (event) => {
  publish(
    'pf_variant_redirect',
    redirectPayload(event.detail, previousSelection)
  );
});
